import { useMemo, useState } from 'react'

import Link from 'next/link'
import parse from 'html-react-parser'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Components */
import ContactModal from 'src/components/ContactModal/ContactModal'
import { SeoBreadcrumbJsonLd } from 'src/components/Breadcrumbs/Breadcrumbs'
import Seo from './Seo'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'

/* Shop-Config */
import shopInfos from 'template/infos'

/* Types */
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type {
  MenuPageBuilderButton,
  MenuPageBuilderContactSubject,
} from 'src/shared/dbTypes/menuPageBuilder'
import type { LandingPageAboutPageSettings } from 'src/shared/dbTypes/landingPageSettings'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

const breadcrumb: BreadcrumbItem[] = [
  { title: 'Über uns' },
]

const getDefaultAboutPageSettings = (): Required<Omit<LandingPageAboutPageSettings, 'heroCta' | 'heroSecondaryCta' | 'contactCta'>> & {
  heroCta: MenuPageBuilderButton
  heroSecondaryCta: MenuPageBuilderButton
  contactCta: MenuPageBuilderButton
} => ({
  enabled: true,
  metaTitle: `Über uns | ${shopInfos.name}`,
  metaDescription: `Erfahren Sie mehr über ${shopInfos.name}.`,
  heroEyebrow: '',
  heroTitle: `Über ${shopInfos.name}`,
  heroBody: `Erfahren Sie mehr über ${shopInfos.name}.`,
  heroImageSrc: '',
  heroImageAlt: '',
  heroMobileImageSrc: '',
  heroMobileImageAlt: '',
  heroCta: {
    label: '',
    href: '',
    action: 'contactModal',
    contactSubject: 'termin',
  },
  heroSecondaryCta: {
    label: '',
    href: '',
  },
  introTitle: '',
  introBodyHtml: '',
  stats: [],
  featureTitle: '',
  featureSubtitle: '',
  features: [],
  storyTitle: '',
  storyBodyHtml: '',
  storyImageSrc: '',
  storyImageAlt: '',
  contactTitle: '',
  contactBody: '',
  contactCta: {
    label: '',
    href: '',
  },
})

const resolveAboutPageSettings = (
  aboutPage?: LandingPageAboutPageSettings
) => {
  const defaults = getDefaultAboutPageSettings()
  return {
    ...defaults,
    ...aboutPage,
    heroCta: aboutPage?.heroCta || defaults.heroCta,
    heroSecondaryCta: aboutPage?.heroSecondaryCta || defaults.heroSecondaryCta,
    contactCta: aboutPage?.contactCta || defaults.contactCta,
    stats: aboutPage?.stats?.length ? aboutPage.stats : defaults.stats,
    features: aboutPage?.features?.length ? aboutPage.features : defaults.features,
  }
}

const isExternalHref = (href?: string) =>
  /^https?:\/\//i.test(String(href || ''))

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    page: {
      background: '#f5f1eb',
      color: '#211c17',
      paddingBottom: theme.spacing(8),
    },
    hero: {
      position: 'relative',
      marginLeft: 'calc(50% - 50vw)',
      width: '100vw',
      minHeight: 520,
      display: 'flex',
      alignItems: 'flex-end',
      backgroundImage: 'linear-gradient(90deg, #2d261f, #615548)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: '#ffffff',
      [theme.breakpoints.down('md')]: {
        minHeight: 440,
        backgroundPosition: 'center top',
      },
    },
    heroBreadcrumbs: {
      position: 'absolute',
      top: theme.spacing(3),
      left: theme.spacing(3),
      right: theme.spacing(3),
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
      color: 'rgba(255,255,255,.78)',
      fontSize: 13,
      fontWeight: 700,
      textShadow: '0 2px 14px rgba(0,0,0,.52)',
      [theme.breakpoints.up('md')]: {
        top: theme.spacing(4),
        left: theme.spacing(8),
        right: theme.spacing(8),
      },
      '& a': {
        color: 'inherit',
        textDecoration: 'none',
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
      '& span:last-child': {
        minWidth: 0,
        overflow: 'hidden',
        color: '#ffffff',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    heroInner: {
      width: '100%',
      padding: theme.spacing(10, 3, 9),
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(8),
        paddingRight: theme.spacing(8),
      },
    },
    heroText: {
      maxWidth: 740,
    },
    eyebrow: {
      textTransform: 'uppercase',
      letterSpacing: 0,
      fontSize: 13,
      fontWeight: 700,
      marginBottom: theme.spacing(1.5),
      color: '#f4d7bd',
    },
    heroTitle: {
      fontWeight: 700,
      lineHeight: 1.03,
      maxWidth: 720,
      [theme.breakpoints.down('md')]: {
        fontSize: 38,
      },
    },
    heroBody: {
      maxWidth: 620,
      marginTop: theme.spacing(2.5),
      fontSize: 19,
      lineHeight: 1.6,
      color: 'rgba(255,255,255,.9)',
      [theme.breakpoints.down('md')]: {
        fontSize: 17,
      },
    },
    heroActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(4),
    },
    primaryButton: {
      background: '#1f5b43',
      color: '#ffffff',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      '&:hover': {
        background: '#184734',
      },
    },
    secondaryButton: {
      color: '#ffffff',
      borderColor: 'rgba(255,255,255,.72)',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      '&:hover': {
        borderColor: '#ffffff',
        background: 'rgba(255,255,255,.1)',
      },
    },
    intro: {
      paddingTop: theme.spacing(7),
      paddingBottom: theme.spacing(5),
      display: 'grid',
      gap: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, .9fr) minmax(0, 1.1fr)',
        alignItems: 'start',
      },
    },
    lead: {
      fontSize: 18,
      lineHeight: 1.8,
      color: '#4f463d',
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    stat: {
      borderTop: '2px solid #6b5948',
      paddingTop: theme.spacing(2),
    },
    statValue: {
      fontWeight: 700,
      color: '#1f5b43',
    },
    sectionBand: {
      marginLeft: 'calc(50% - 50vw)',
      width: '100vw',
      background: '#fffaf5',
      paddingTop: theme.spacing(7),
      paddingBottom: theme.spacing(7),
    },
    grid: {
      display: 'grid',
      gap: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      },
    },
    textBlock: {
      '& h2, & h3': {
        fontWeight: 700,
      },
      '& p': {
        color: '#4f463d',
        lineHeight: 1.8,
      },
    },
    showroom: {
      display: 'grid',
      gap: theme.spacing(5),
      alignItems: 'center',
      paddingTop: theme.spacing(7),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      },
    },
    showroomImage: {
      width: '100%',
      aspectRatio: '4 / 3',
      objectFit: 'cover',
      display: 'block',
    },
    showroomImageWrap: {
      lineHeight: 0,
      position: 'relative',
    },
    contactBand: {
      marginTop: theme.spacing(7),
      padding: theme.spacing(5),
      background: '#1f5b43',
      color: '#ffffff',
      display: 'grid',
      gap: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
      },
    },
    contactText: {
      color: 'rgba(255,255,255,.88)',
      marginTop: theme.spacing(1),
    },
    contactButton: {
      background: '#ffffff',
      color: '#1f5b43',
      '&:hover': {
        background: '#f0e7db',
      },
    },
  })
)

interface Props {
  aboutPage?: LandingPageAboutPageSettings
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const UeberUns: React.FC<Props> = ({ aboutPage, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [contactModalSubject, setContactModalSubject] = useState<MenuPageBuilderContactSubject>('termin')
  const settings = resolveAboutPageSettings(aboutPage)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const heroBackgroundImage = isMobile && settings.heroMobileImageSrc
    ? settings.heroMobileImageSrc
    : settings.heroImageSrc

  const openContactModal = (subject: MenuPageBuilderContactSubject = 'termin') => {
    setContactModalSubject(subject)
    setContactModalOpen(true)
  }

  const renderCtaButton = (
    button: MenuPageBuilderButton,
    variant: 'contained' | 'outlined',
    className: string
  ) => {
    if (!button.label) return null

    if (button.action === 'contactModal' || button.href === '#contact-modal' || button.href === '#kontaktmodal') {
      return <Button
        type='button'
        className={className}
        variant={variant}
        onClick={() => openContactModal(button.contactSubject || 'termin')}
      >
        {button.label}
      </Button>
    }

    if (!button.href) return null

    if (isExternalHref(button.href)) {
      return <Button
        component='a'
        href={button.href}
        target='_blank'
        rel='noopener noreferrer'
        className={className}
        variant={variant}
      >
        {button.label}
      </Button>
    }

    return <Link href={button.href} passHref legacyBehavior>
      <Button component='a' className={className} variant={variant}>
        {button.label}
      </Button>
    </Link>
  }

  return <main className={classes.page}>
    <Seo aboutPage={settings} />
    <SeoBreadcrumbJsonLd items={breadcrumb} />

    <section
      className={classes.hero}
      style={{
        backgroundImage: heroBackgroundImage
          ? `linear-gradient(90deg, rgba(18, 14, 10, .82), rgba(18, 14, 10, .34)), url(${heroBackgroundImage})`
          : 'linear-gradient(90deg, #2d261f, #615548)',
      }}
    >
      <AiGeneratedBadge aiGenerated={isAiGeneratedLandingImage(heroBackgroundImage)} placement='top-right' />
      <nav className={classes.heroBreadcrumbs} aria-label='Navigationspfad'>
        <Link href='/' passHref legacyBehavior>
          <a>Home</a>
        </Link>
        <span aria-hidden='true'>/</span>
        <span>Über uns</span>
      </nav>

      <div className={classes.heroInner}>
        <div className={classes.heroText}>
          {settings.heroEyebrow && <div className={classes.eyebrow}>{settings.heroEyebrow}</div>}
          <Typo component='h1' variant='h2' className={classes.heroTitle}>
            {settings.heroTitle}
          </Typo>
          <Typo component='p' className={classes.heroBody}>
            {settings.heroBody}
          </Typo>
          <div className={classes.heroActions}>
            {renderCtaButton(settings.heroCta, 'contained', classes.primaryButton)}
            {renderCtaButton(settings.heroSecondaryCta, 'outlined', classes.secondaryButton)}
          </div>
        </div>
      </div>
    </section>

    <Container maxWidth='lg'>
      <section className={classes.intro}>
        <div className={classes.textBlock}>
          <Typo component='h2' variant='h4' gutterBottom>
            {settings.introTitle}
          </Typo>
          <div className={classes.lead}>
            {parse(settings.introBodyHtml || '')}
          </div>
        </div>
        <div className={classes.stats}>
          {settings.stats.map((stat, index) =>
            <div key={`${stat.value}-${index}`} className={classes.stat}>
              <Typo component='div' variant='h4' className={classes.statValue}>{stat.value}</Typo>
              <Typo component='p'>{stat.label}</Typo>
            </div>
          )}
        </div>
      </section>
    </Container>

    <section className={classes.sectionBand}>
      <Container maxWidth='lg'>
        {(settings.featureTitle || settings.featureSubtitle) &&
          <div className={classes.textBlock} style={{ marginBottom: 32, maxWidth: 760 }}>
            {settings.featureTitle &&
              <Typo component='h2' variant='h4' gutterBottom>
                {settings.featureTitle}
              </Typo>
            }
            {settings.featureSubtitle &&
              <Typo component='p'>{settings.featureSubtitle}</Typo>
            }
          </div>
        }
        <div className={classes.grid}>
          {settings.features.map((feature, index) =>
            <div key={`${feature.title}-${index}`} className={classes.textBlock}>
              <Typo component='h3' variant='h5' gutterBottom>
                {feature.title}
              </Typo>
              {parse(feature.body)}
            </div>
          )}
        </div>
      </Container>
    </section>

    <Container maxWidth='lg'>
      {(settings.storyImageSrc || settings.storyTitle || settings.storyBodyHtml) &&
        <section className={classes.showroom}>
          {settings.storyImageSrc &&
            <div className={classes.showroomImageWrap}>
              <img
                src={settings.storyImageSrc}
                alt={settings.storyImageAlt || 'Bild zu Über uns'}
                className={classes.showroomImage}
                loading='lazy'
              />
              <AiGeneratedBadge
                aiGenerated={isAiGeneratedLandingImage(settings.storyImageSrc)}
                placement='top-right'
              />
            </div>
          }
          <div className={classes.textBlock}>
            {settings.storyTitle &&
              <Typo component='h2' variant='h4' gutterBottom>
                {settings.storyTitle}
              </Typo>
            }
            {parse(settings.storyBodyHtml || '')}
          </div>
        </section>
      }

      {(settings.contactTitle || settings.contactBody || settings.contactCta.label) &&
        <section className={classes.contactBand}>
          <div>
            {settings.contactTitle &&
              <Typo component='h2' variant='h5'>
                {settings.contactTitle}
              </Typo>
            }
            {settings.contactBody &&
              <Typo component='p' className={classes.contactText}>
                {settings.contactBody}
              </Typo>
            }
          </div>
          {renderCtaButton(settings.contactCta, 'contained', classes.contactButton)}
        </section>
      }
    </Container>

    <ContactModal
      open={contactModalOpen}
      initialSubject={contactModalSubject}
      onClose={() => setContactModalOpen(false)}
    />
  </main>
}

export default UeberUns
